import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Switch,
  Tabs,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip, LocalizedFormControlLabel as FormControlLabel, LocalizedInputLabel as InputLabel, LocalizedMenuItem as MenuItem, LocalizedTab as Tab, LocalizedTableCell as TableCell, LocalizedTextField as TextField } from '../i18n/LocalizedMui';
import Typography from '../i18n/LocalizedTypography';
import axios from 'axios';
import NorthIndianChart from './NorthIndianChart';
import BhavaChalitChart from './BhavaChalitChart';
import { useAuth } from '../../contexts/AuthContext';
import { hasPremiumSubscription } from '../../utils/subscription';

import { calculateRuntimeDasha, formatDashaDate } from '../../utils/dashaCalculator';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getHouseShort, getPlanetAbbr, normalizeAstroKey } from '../../i18n/astrology';
import { useCurrentTransitOverlay } from '../../hooks/useCurrentTransitOverlay';
import { FREE_LIFE_EVENT_KEYS, lifeEvents } from '../../utils/lifeEvents';
import { translateInline } from '../../i18n/inlineTranslations';
import { normalizeLanguage } from '../../i18n/languages';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import { formatDisplayDate, formatDisplayDateTime } from '../../utils/dateDisplay';
import KpFruitfulSignificatorPanel from './KpFruitfulSignificatorPanel';
import KpNadiTransitScanner from './KpNadiTransitScanner';
import DashaAnalysisPanel, { dashaAnalysisLabel } from '../dasha/DashaAnalysisPanel';
import FaladeshSystemPanel from '../faladesh/FaladeshSystemPanel';
import EventAnalysisPanel from '../nadi/EventAnalysisPanel';
import { translateNadiEventText } from '../../data/nadiEventTranslations';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const NEW_DELHI_LOCATION = {
  city: 'New Delhi',
  latitude: 28.6139,
  longitude: 77.2090,
  timezoneOffset: 330,
};

const localGuidanceText = (value: any, language: string) => {
  const selected = value && typeof value === 'object'
    ? value[language] || (language === 'hi' ? value.hi : value.en) || value.en || value.hi || ''
    : value || '';
  return translateInline(language, String(selected));
};

interface KPChartProps {
  chartData: any;
  size?: number;
  selectedLifeEvent?: string;
  getHouseHighlight?: (houseNumber: number) => string | null;
}

type DashaEntry = {
  lord?: string;
  start?: string | Date;
  end?: string | Date;
  years?: number;
  antardashas?: DashaEntry[];
  pratyantardashas?: DashaEntry[];
};

type PlanetRow = {
  planet: string;
  starLord: string;
  subLord: string;
  subSubLord: string;
  all: number[];
  level1: number[];
  level2: number[];
  level3: number[];
  level4: number[];
};

type KpEphemerisPlanet = {
  key: string;
  name: string;
  signAbbr: string;
  sign: string;
  degree: { formatted: string };
  starLord: string;
  subLord: string;
  isRetrograde?: boolean;
};

type KpEphemerisRow = {
  date: string;
  dayName: string;
  planets: Record<string, KpEphemerisPlanet>;
};

type KpEphemerisData = {
  month: string;
  time: string;
  timezone: string;
  ayanamshaMode: string;
  planets: { key: string; name: string }[];
  rows: KpEphemerisRow[];
};

type KpTransitPoint = {
  key?: string;
  name?: string;
  label?: string;
  sign?: string;
  signAbbr?: string;
  house?: number | null;
  degree?: { formatted?: string };
  signLord?: string;
  starLord?: string;
  subLord?: string;
  subSubLord?: string;
  isRetrograde?: boolean;
};

type KpTransitData = {
  city?: string;
  date: string;
  time: string;
  timezoneOffset: number;
  ayanamshaMode: string;
  rulingPlanets?: any;
  planets: KpTransitPoint[];
  cusps: KpTransitPoint[];
};

const TAB_KEYS = [
  'kp.kpChart',
  'kp.nakshatraNadi',
  'kp.eventAnalysis',
  'kp.dashaAnalysis',
  'nav.faladesh',
  'kp.guidance',
  'kp.muhurat',
  'kp.dashas',
  'kp.houseSignificator',
  'kp.planetSignificator',
  'kp.ephemeris',
  'kp.transit',
  'kp.fourLevel',
  'kp.aspects',
  'kp.hitTheory',
  'kp.fruitfulSignificators',
];

const TAB_VALUE_BY_KEY: Record<string, number> = {
  'kp.kpChart': 0,
  'kp.nakshatraNadi': 1,
  'kp.eventAnalysis': 16,
  'kp.guidance': 2,
  'kp.muhurat': 3,
  'kp.dashas': 4,
  'kp.houseSignificator': 5,
  'kp.planetSignificator': 6,
  'kp.ephemeris': 7,
  'kp.transit': 8,
  'kp.fourLevel': 9,
  'kp.aspects': 10,
  'kp.hitTheory': 11,
  'kp.fruitfulSignificators': 12,
  'kp.dashaAnalysis': 13,
  'nav.faladesh': 15,
};

const TAB_LABEL_DEFAULTS: Record<string, string> = {
  'kp.tenantedPlanets': 'TENANTED PLANETS',
  'kp.nakshatraNadi': 'NAKSHATRA NADI',
  'kp.eventAnalysis': 'EVENT ANALYSIS',
  'kp.ephemeris': 'KP EPHEMERIS',
  'kp.transit': 'KP TRANSIT',
  'kp.guidance': 'KP NOTES',
  'kp.muhurat': 'KP FACTORS',
  'kp.fruitfulSignificators': 'FRUITFUL SIGNIFICATORS',
  'kp.dashaAnalysis': 'DASHA ANALYSIS',
};

const PLANET_ORDER = ['SUN', 'MOON', 'MARS', 'MERCURY', 'JUPITER', 'VENUS', 'SATURN', 'RAHU', 'KETU'];

const HIT_DEGREE_OPTIONS = [30, 45, 60, 90, 120, 180];

const HIT_RANGES = [
  { name: 'Positive 30°', min: 27, max: 33, type: 'positive' },
  { name: 'Support 60°', min: 55, max: 65, type: 'positive' },
  { name: 'Trine 120°', min: 112, max: 128, type: 'positive' },
  { name: 'Problem 45°', min: 42, max: 48, type: 'negative' },
  { name: 'Stress 90°', min: 85, max: 95, type: 'negative' },
  { name: 'Killer 180°', min: 172, max: 188, type: 'negative' },
];

const HIIT_TARGETS: Record<string, number[]> = {
  marriage: [2, 7, 11],
  career: [2, 6, 10, 11],
  education: [2, 4, 5, 9, 11],
  health: [1, 6, 8, 12],
  wealth: [2, 5, 9, 11],
  property: [4, 11],
};

const ASPECT_DEFS = [
  { name: 'Conjunction', degree: 0, orb: 8 },
  { name: 'Sextile', degree: 60, orb: 6 },
  { name: 'Square', degree: 90, orb: 6 },
  { name: 'Trine', degree: 120, orb: 6 },
  { name: 'Opposition', degree: 180, orb: 8 },
];

const KP_DASHA_ORDER = ['KETU', 'VENUS', 'SUN', 'MOON', 'MARS', 'RAHU', 'JUPITER', 'SATURN', 'MERCURY'];
const NADI_PLANETS_ORDER = PLANET_ORDER;
const NAKSHATRA_NAMES = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra',
  'Punarvasu', 'Pushya', 'Ashlesha', 'Magha', 'Purva Phalguni', 'Uttara Phalguni',
  'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha', 'Jyeshtha',
  'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta',
  'Shatabhisha', 'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati',
];

const KP_DASHA_YEARS: Record<string, number> = {
  KETU: 7,
  VENUS: 20,
  SUN: 6,
  MOON: 10,
  MARS: 7,
  RAHU: 18,
  JUPITER: 16,
  SATURN: 19,
  MERCURY: 17,
};

const getKPSubAndSubSubLord = (longitude: number) => {
  const safeLon = ((longitude % 360) + 360) % 360;
  const totalMinutes = safeLon * 60;
  const nakMinutes = 800;
  const nakIndex = Math.floor(totalMinutes / nakMinutes);
  const minutesIntoNak = totalMinutes - (nakIndex * nakMinutes);

  const starStartIndex = nakIndex % 9;
  const subOrder = [
    ...KP_DASHA_ORDER.slice(starStartIndex),
    ...KP_DASHA_ORDER.slice(0, starStartIndex),
  ];

  let accumulatedSub = 0;
  let subLord = subOrder[0];
  let subLordSpan = (KP_DASHA_YEARS[subLord] / 120) * nakMinutes;

  for (let i = 0; i < subOrder.length; i++) {
    const planet = subOrder[i];
    const span = (KP_DASHA_YEARS[planet] / 120) * nakMinutes;
    if (minutesIntoNak >= accumulatedSub && minutesIntoNak < accumulatedSub + span) {
      subLord = planet;
      subLordSpan = span;
      break;
    }
    accumulatedSub += span;
  }

  const minutesIntoSub = minutesIntoNak - accumulatedSub;
  const sslStartIndex = KP_DASHA_ORDER.indexOf(subLord);
  const sslOrder = [
    ...KP_DASHA_ORDER.slice(sslStartIndex),
    ...KP_DASHA_ORDER.slice(0, sslStartIndex),
  ];

  let accumulatedSSL = 0;
  let subSubLord = subLord;
  for (let i = 0; i < sslOrder.length; i++) {
    const planet = sslOrder[i];
    const sslSpan = (KP_DASHA_YEARS[planet] / 120) * subLordSpan;
    if (minutesIntoSub >= accumulatedSSL && minutesIntoSub < accumulatedSSL + sslSpan) {
      subSubLord = planet;
      break;
    }
    accumulatedSSL += sslSpan;
  }

  return { subLord, subSubLord };
};

const normalizePlanet = (name: string) => String(name || '').trim().toUpperCase();
const toTitlePlanet = (name: string) => {
  const normalized = normalizePlanet(name).toLowerCase();
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '';
};

const asNumberArray = (value: any): number[] => {
  if (!Array.isArray(value)) return [];
  return value.map((n) => Number(n)).filter((n) => Number.isFinite(n));
};

const uniqueNumbers = (values: number[]) => Array.from(new Set(values));

const normalizeDeg = (deg: number) => ((deg % 360) + 360) % 360;

const getNakshatraInfo = (longitude: number) => {
  const degree = normalizeDeg(longitude);
  const index = Math.floor(degree / (360 / 27));
  return {
    index: index + 1,
    name: NAKSHATRA_NAMES[index],
    lord: toTitlePlanet(KP_DASHA_ORDER[index % 9]),
  };
};

const getPlanetHouse = (planet: any, chartData: any) => {
  if (planet?.bhavaChalitHouse) return planet.bhavaChalitHouse;
  const longitude = planet?.longitude;
  const cusps = chartData?.houses?.cusps;
  if (typeof longitude !== 'number' || !cusps) return planet?.house || null;

  const lon = normalizeDeg(longitude);
  for (let house = 1; house <= 12; house++) {
    const start = normalizeDeg(cusps[house]?.longitude ?? 0);
    const nextHouse = house === 12 ? 1 : house + 1;
    const end = normalizeDeg(cusps[nextHouse]?.longitude ?? 0);
    if (end > start ? lon >= start && lon < end : lon >= start || lon < end) return house;
  }
  return planet?.house || null;
};

const calculateTenantData = (chartData: any) => {
  const planetTenants: Record<string, any> = {};
  const houseTenantSignificators: Record<number, any> = {};
  const planets = (Object.values(chartData?.planets || {}) as any[])
    .filter((planet) => PLANET_ORDER.includes(normalizePlanet(planet?.name)));

  PLANET_ORDER.forEach((key) => {
    const planet = toTitlePlanet(key);
    planetTenants[planet] = { planet, isTenanted: false, isUntenanted: true, tenants: [], tenantCount: 0 };
  });

  planets.forEach((planet) => {
    if (!planet || typeof planet.longitude !== 'number') return;
    const nakshatra = getNakshatraInfo(planet.longitude);
    const starLord = nakshatra.lord;
    if (!planetTenants[starLord]) planetTenants[starLord] = { planet: starLord, isTenanted: false, isUntenanted: true, tenants: [], tenantCount: 0 };
    planetTenants[starLord].tenants.push({
      planet: planet.name,
      longitude: planet.longitude,
      house: getPlanetHouse(planet, chartData),
      nakshatra: nakshatra.name,
      nakshatraLord: starLord,
    });
  });

  Object.values(planetTenants).forEach((item: any) => {
    item.tenantCount = item.tenants.length;
    item.isTenanted = item.tenantCount > 0;
    item.isUntenanted = item.tenantCount === 0;
  });

  for (let house = 1; house <= 12; house++) {
    const occupants = planets
      .filter((planet) => getPlanetHouse(planet, chartData) === house)
      .map((planet) => planet.name);
    const planetsInStarOfOccupants: any[] = [];
    occupants.forEach((occupant) => {
      (planetTenants[occupant]?.tenants || []).forEach((tenant: any) => {
        planetsInStarOfOccupants.push({ planet: tenant.planet, starLord: occupant, nakshatra: tenant.nakshatra });
      });
    });
    houseTenantSignificators[house] = { house, occupants, planetsInStarOfOccupants };
  }

  return { planetTenants, houseTenantSignificators };
};

const getTenantRecord = (planetTenants: any, planetName: string) => (
  planetTenants?.[planetName] ||
  Object.values(planetTenants || {}).find((item: any) => normalizePlanet(item?.planet) === normalizePlanet(planetName))
);

const isUntenantedPlanet = (tenantRecord: any) => {
  if (!tenantRecord) return false;
  if (typeof tenantRecord.isUntenanted === 'boolean') return tenantRecord.isUntenanted;
  if (typeof tenantRecord.tenantCount === 'number') return tenantRecord.tenantCount === 0;
  if (Array.isArray(tenantRecord.tenants)) return tenantRecord.tenants.length === 0;
  return tenantRecord.isTenanted === false;
};

const getShortestAngle = (a: number, b: number) => {
  let diff = Math.abs(normalizeDeg(a) - normalizeDeg(b));
  if (diff > 180) diff = 360 - diff;
  return diff;
};

const detectHit = (angle: number) => {
  for (const range of HIT_RANGES) {
    if (angle >= range.min && angle <= range.max) {
      return range;
    }
  }
  return { name: 'No Hit', type: 'neutral' };
};

const formatArc = (degrees: number) => {
  const safe = normalizeDeg(degrees);
  const d = Math.floor(safe);
  const minutesFloat = (safe - d) * 60;
  const m = Math.floor(minutesFloat);
  const s = Math.floor((minutesFloat - m) * 60);
  return `${String(d).padStart(3, '0')}-${String(m).padStart(2, '0')}-${String(s).padStart(2, '0')}`;
};

const getAspect = (a: number, b: number) => {
  let diff = Math.abs(normalizeDeg(a) - normalizeDeg(b));
  if (diff > 180) diff = 360 - diff;

  for (const def of ASPECT_DEFS) {
    const orb = Math.abs(diff - def.degree);
    if (orb <= def.orb) {
      return { aspect: def.name, aspectDegree: def.degree, orb: Number(orb.toFixed(2)) };
    }
  }
  return null;
};

const formatMonthInput = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const formatDateLabel = (dateValue: string) => {
  const date = new Date(`${dateValue}T00:00:00Z`);
  return formatDisplayDate(date, { timeZone: 'UTC' });
};

const formatMonthLabel = (monthValue: string) => {
  const [year, month] = String(monthValue || '').split('-').map((part) => Number(part));
  if (!Number.isFinite(year) || !Number.isFinite(month)) return monthValue;
  return new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
};

const shortLord = (value?: string) => {
  const map: Record<string, string> = {
    Sun: 'Sun',
    Moon: 'Moo',
    Mars: 'Mar',
    Mercury: 'Mer',
    Jupiter: 'Jup',
    Venus: 'Ven',
    Saturn: 'Sat',
    Rahu: 'Rah',
    Ketu: 'Ket',
  };
  return value ? map[value] || value.slice(0, 3) : '-';
};

const KpPlanetCell: React.FC<{ planet?: KpEphemerisPlanet }> = ({ planet }) => {
  if (!planet) return <Typography variant="body2">-</Typography>;

  return (
    <Box sx={{ minWidth: 150 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: '#143728', whiteSpace: 'nowrap' }}>
        {planet.signAbbr} | {planet.degree?.formatted || '-'} {planet.isRetrograde ? 'R' : ''}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', whiteSpace: 'nowrap' }}>
        Stl: {shortLord(planet.starLord)} | Sub: {shortLord(planet.subLord)}
      </Typography>
    </Box>
  );
};

const KpEphemerisPanel: React.FC<{ chartData: any }> = ({ chartData }) => {
  const { t } = useTranslation();
  const [month, setMonth] = useState(formatMonthInput(new Date()));
  const [time, setTime] = useState('00:00:00');
  const [data, setData] = useState<KpEphemerisData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timezone = chartData?.birthDetails?.placeOfBirth?.timezone || '+05:30';

  const loadEphemeris = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE_URL}/charts/kp/ephemeris`, {
        params: { month, time, timezone, ayanamsha: 'KP' },
      });
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to load KP ephemeris');
    } finally {
      setLoading(false);
    }
  }, [month, time, timezone]);

  React.useEffect(() => {
    loadEphemeris();
  }, [loadEphemeris]);

  return (
    <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 2.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {t('kp.ephemeris', { defaultValue: 'KP Ephemeris' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('kp.kpAyanamshaHint', { defaultValue: 'KP ayanamsha is used for sign, star lord and sub lord.' })}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
            <TextField
              label="Month"
              type="month"
              size="small"
              value={month}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setMonth(e.target.value)}
            />
            <TextField
              label="Time"
              type="time"
              size="small"
              value={time.slice(0, 5)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
              onChange={(e) => setTime(`${e.target.value}:00`)}
            />
            <Button variant="contained" onClick={loadEphemeris} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`Month: ${formatMonthLabel(data?.month || month)}`} size="small" />
          <Chip label={`Time: ${data?.time || time}`} size="small" />
          <Chip label={`Timezone: ${data?.timezone || timezone}`} size="small" />
          <Chip label={`Ayanamsha: ${data?.ayanamshaMode || 'KP'}`} size="small" color="primary" variant="outlined" />
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 680 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 1900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 600, position: 'sticky', left: 0, zIndex: 3, minWidth: 165 }}>
                  Date
                </TableCell>
                {(data?.planets || []).map((planet) => (
                  <TableCell key={planet.key} sx={{ bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 600, minWidth: 155 }}>
                    {planet.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={26} />
                  </TableCell>
                </TableRow>
              ) : (
                (data?.rows || []).map((row) => (
                  <TableRow key={row.date} hover>
                    <TableCell
                      sx={{
                        bgcolor: 'primary.light',
                        color: 'primary.dark',
                        fontWeight: 600,
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        borderRight: '1px solid rgba(0,0,0,0.14)',
                        minWidth: 165,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDateLabel(row.date)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {row.dayName}
                      </Typography>
                    </TableCell>
                    {(data?.planets || []).map((planet) => (
                      <TableCell key={`${row.date}-${planet.key}`} sx={{ verticalAlign: 'top', borderRight: '1px solid rgba(0,0,0,0.08)' }}>
                        <KpPlanetCell planet={row.planets[planet.key]} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
};

const parseTimezoneOffset = (value: any) => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const match = value.trim().match(/^([+-])?(\d{1,2})(?::?(\d{2}))?$/);
    if (match) {
      const sign = match[1] === '-' ? -1 : 1;
      return sign * ((Number(match[2]) || 0) * 60 + (Number(match[3]) || 0));
    }
  }
  return 330;
};

const getLocalDateTimeForOffset = (timezoneOffset: number) => {
  const local = new Date(Date.now() + timezoneOffset * 60000);
  return {
    date: local.toISOString().slice(0, 10),
    time: local.toISOString().slice(11, 19),
  };
};

const getDefaultTransitLocation = (defaultCity: any) => {
  const latitude = Number(defaultCity?.coordinates?.latitude);
  const longitude = Number(defaultCity?.coordinates?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return NEW_DELHI_LOCATION;
  return {
    city: defaultCity.displayName || defaultCity.name || 'New Delhi',
    latitude,
    longitude,
    timezoneOffset: parseTimezoneOffset(defaultCity.timezone?.offset),
  };
};

const rulingPlanetItems = (rulingPlanets: any) => [
  { label: 'Lg Sign', title: 'Lagna Sign Lord', value: rulingPlanets?.lagnaLord },
  { label: 'Lg Star', title: 'Lagna Star Lord', value: rulingPlanets?.lagnaStarLord },
  { label: 'Mo Sign', title: 'Moon Sign Lord', value: rulingPlanets?.moonSignLord },
  { label: 'Mo Star', title: 'Moon Star Lord', value: rulingPlanets?.moonStarLord },
  { label: 'Day', title: 'Day Lord', value: rulingPlanets?.dayLord },
];

const KpTransitPanel: React.FC<{ chartData: any }> = ({ chartData }) => {
  const { t } = useTranslation();
  const { defaultCity } = useUserPreferences();
  const transitLocation = useMemo(() => getDefaultTransitLocation(defaultCity), [defaultCity]);
  const timezoneOffset = transitLocation.timezoneOffset;
  const initialDateTime = useMemo(() => getLocalDateTimeForOffset(timezoneOffset), [timezoneOffset]);
  const [date, setDate] = useState(initialDateTime.date);
  const [time, setTime] = useState(initialDateTime.time);
  const [data, setData] = useState<KpTransitData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadTransit = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_BASE_URL}/charts/kp/transit`, {
        city: transitLocation.city,
        latitude: transitLocation.latitude,
        longitude: transitLocation.longitude,
        timezoneOffset,
        date,
        time,
        ayanamsha: 'KP',
      });
      setData(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Unable to load KP transit');
    } finally {
      setLoading(false);
    }
  }, [date, time, timezoneOffset, transitLocation]);

  React.useEffect(() => {
    loadTransit();
  }, [loadTransit]);

  const lordAbbr = (name?: string) => getPlanetAbbr(t, name || '-');
  const lordPair = (left?: string, right?: string) => `${lordAbbr(left)} | ${lordAbbr(right)}`;
  const nodesText = (data?.rulingPlanets?.nodes || [])
    .map((node: any) => `${node.name}: ${lordPair(node.signLord, node.starLord)}`)
    .join('  ');

  return (
    <Card sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, md: 3 } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 2.5 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('kp.transit', { defaultValue: 'KP Transit' })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('kp.kpTransitHint', { defaultValue: 'Transit planets, cusps and ruling planets using KP ayanamsha.' })}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
            <TextField label="Date" type="date" size="small" value={date} InputLabelProps={{ shrink: true }} onChange={(e) => setDate(e.target.value)} />
            <TextField label="Time" type="time" size="small" value={time.slice(0, 5)} InputLabelProps={{ shrink: true }} inputProps={{ step: 60 }} onChange={(e) => setTime(`${e.target.value}:00`)} />
            <Button variant="contained" onClick={loadTransit} disabled={loading}>
              {loading ? 'Loading...' : 'Refresh'}
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
          <Chip label={`${data?.date || date} ${data?.time || time}`} size="small" />
          <Chip label={`Timezone: ${data?.timezoneOffset ?? timezoneOffset}`} size="small" />
          <Chip label={`Ayanamsha: ${data?.ayanamshaMode || 'KP'}`} size="small" color="primary" variant="outlined" />
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {data?.rulingPlanets ? (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Lagna SGN | STL</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Moon SGN | STL</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Day Lord</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Nodes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>{lordPair(data.rulingPlanets.lagnaLord, data.rulingPlanets.lagnaStarLord)}</TableCell>
                  <TableCell>{lordPair(data.rulingPlanets.moonSignLord, data.rulingPlanets.moonStarLord)}</TableCell>
                  <TableCell>{lordAbbr(data.rulingPlanets.dayLord)}</TableCell>
                  <TableCell>{nodesText || '-'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}

        {loading && !data ? (
          <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={26} /></Box>
        ) : (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 7 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>{t('kp.transitPlanets', { defaultValue: 'Transit Planets' })}</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Planet', 'Sign', 'H', 'Degree', 'SGN | STL | SUB | SSL'].map((label) => (
                        <TableCell key={label} sx={{ fontWeight: 600 }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.planets || []).map((planet) => (
                      <TableRow key={planet.key || planet.name}>
                        <TableCell sx={{ fontWeight: 600 }}>{planet.name}{planet.isRetrograde ? ' R' : ''}</TableCell>
                        <TableCell>{planet.signAbbr || planet.sign}</TableCell>
                        <TableCell>{planet.house || '-'}</TableCell>
                        <TableCell>{planet.degree?.formatted || '-'}</TableCell>
                        <TableCell>{lordAbbr(planet.signLord)} | {lordAbbr(planet.starLord)} | {lordAbbr(planet.subLord)} | {lordAbbr(planet.subSubLord)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={{ xs: 12, lg: 5 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>{t('kp.transitCusps', { defaultValue: 'Transit Cusps' })}</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {['Cusp', 'Sign', 'Degree', 'SGN | STL | SUB | SSL'].map((label) => (
                        <TableCell key={label} sx={{ fontWeight: 600 }}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(data?.cusps || []).map((cusp) => (
                      <TableRow key={cusp.label || cusp.name}>
                        <TableCell sx={{ fontWeight: 600 }}>{cusp.label}</TableCell>
                        <TableCell>{cusp.signAbbr || cusp.sign}</TableCell>
                        <TableCell>{cusp.degree?.formatted || '-'}</TableCell>
                        <TableCell>{lordAbbr(cusp.signLord)} | {lordAbbr(cusp.starLord)} | {lordAbbr(cusp.subLord)} | {lordAbbr(cusp.subSubLord)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

const PremiumFeatureLock: React.FC<{ title: string; message: string; buttonLabel: string; onUpgrade: () => void }> = ({
  title,
  message,
  buttonLabel,
  onUpgrade,
}) => (
  <Box sx={{ textAlign: 'center', py: 3, px: 2 }}>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {message}
    </Typography>
    <Button variant="contained" color="warning" onClick={onUpgrade}>
      {buttonLabel}
    </Button>
  </Box>
);

const KPChart: React.FC<KPChartProps> = ({ chartData, size }) => {
  const { t, i18n } = useTranslation();
  const language = normalizeLanguage(i18n.resolvedLanguage || i18n.language);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const chartDisplaySize = size ?? (isSmallScreen ? 320 : isMediumScreen ? 420 : 500);
  const [tabIndex, setTabIndex] = useState(0);
  const [rotationHouse, setRotationHouse] = useState(1);
  const [showTransitOverlay, setShowTransitOverlay] = useState(false);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState('none');
  const { transitChart, loading: transitLoading, error: transitError } = useCurrentTransitOverlay(chartData, showTransitOverlay);
  const [selectedHitPlanet, setSelectedHitPlanet] = useState<string>('JUPITER');
  const [selectedHitDegree, setSelectedHitDegree] = useState<number>(90);
  const [kpGuidance, setKpGuidance] = useState<any>(null);
  const [kpGuidanceLoading, setKpGuidanceLoading] = useState(false);
  const [kpGuidanceError, setKpGuidanceError] = useState<string | null>(null);
  const [kpMuhurat, setKpMuhurat] = useState<any>(null);
  const [kpMuhuratLoading, setKpMuhuratLoading] = useState(false);
  const [kpMuhuratError, setKpMuhuratError] = useState<string | null>(null);
  const [currentRulingPlanets, setCurrentRulingPlanets] = useState<any>(null);
  const [currentRulingError, setCurrentRulingError] = useState<string | null>(null);

  const [selectedMahadashaKey, setSelectedMahadashaKey] = useState<string | null>(null);
  const [selectedAntardashaKey, setSelectedAntardashaKey] = useState<string | null>(null);
  const [selectedPratyantardashaKey, setSelectedPratyantardashaKey] = useState<string | null>(null);

  const kpData = useMemo(() => chartData?.kpAnalysis || chartData?.nadiAnalysis || {}, [chartData]);
  const nadi = useMemo(() => chartData?.nadiAnalysis || {}, [chartData]);

  const kpSignificatorMap = useMemo(() => kpData?.kpPlanetarySignificators || {}, [kpData]);
  const derivedTenantData = useMemo(() => calculateTenantData(chartData), [chartData]);
  const planetTenants = kpData?.planetTenants || kpData?.tenantedPlanets || derivedTenantData.planetTenants;
  const { state: authState } = useAuth();
  const { defaultCity } = useUserPreferences();
  const rulingLocation = useMemo(() => getDefaultTransitLocation(defaultCity), [defaultCity]);
  const isPremiumUser = hasPremiumSubscription(authState.user);
  const navigate = useNavigate();
  const tp = (name: string) => {
    const key = normalizeAstroKey(name);
    return t(`astrology.planets.${key}`, { defaultValue: name || '-' });
  };
  const planetShort = useCallback((name: string) => getPlanetAbbr(t, name || '-'), [t]);
  const lordText = (name: string) => (name && name !== '-' ? tp(name) : '-');
  const canUseSelectedLifeEvent = isPremiumUser || FREE_LIFE_EVENT_KEYS.has(selectedLifeEvent);
  const effectiveLifeEventKey = canUseSelectedLifeEvent ? selectedLifeEvent : 'none';
  const selectedLifeEventDefinition = lifeEvents[effectiveLifeEventKey] || lifeEvents.none;
  const getLifeEventHouseStyle = (house: number) => {
    if (effectiveLifeEventKey === 'none') return { color: 'text.primary', bgcolor: 'transparent', fontWeight: 500 };
    if (selectedLifeEventDefinition.goodHouses.includes(house)) return { color: '#fff', bgcolor: '#4caf50', fontWeight: 600 };
    if (selectedLifeEventDefinition.badHouses.includes(house)) return { color: '#fff', bgcolor: '#f44336', fontWeight: 600 };
    if (selectedLifeEventDefinition.goodSupportiveHouses.includes(house)) return { color: '#fff', bgcolor: '#2196f3', fontWeight: 600 };
    if (selectedLifeEventDefinition.badSupportiveHouses.includes(house)) return { color: '#fff', bgcolor: '#8d6e63', fontWeight: 600 };
    return { color: 'text.primary', bgcolor: 'transparent', fontWeight: 500 };
  };
  const renderLifeEventHouses = (houses: number[], occupiedHouse?: number) => {
    if (!houses?.length) return '-';

    return houses.map((house, index) => {
      const style = getLifeEventHouseStyle(house);
      const isHighlighted = style.bgcolor !== 'transparent';
      return (
        <Box
          key={`${house}-${index}`}
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: isHighlighted ? 22 : 'auto',
            height: isHighlighted ? 22 : 'auto',
            mr: 0.5,
            px: isHighlighted ? 0.5 : 0,
            borderRadius: isHighlighted ? '50%' : 0,
            bgcolor: style.bgcolor,
            color: style.color,
            fontWeight: Number(house) === Number(occupiedHouse) ? 800 : style.fontWeight,
          }}
        >
          {house}
        </Box>
      );
    });
  };
  const renderPremiumLock = (title: string, message: string) => (
    <PremiumFeatureLock title={title} message={message} buttonLabel={t('common.upgrade')} onUpgrade={() => navigate('/upgrade')} />
  );
  useEffect(() => {
    const loadCurrentRulingPlanets = async () => {
      try {
        setCurrentRulingError(null);
        const localNow = getLocalDateTimeForOffset(rulingLocation.timezoneOffset);
        const response = await axios.post(`${API_BASE_URL}/charts/ruling-planets`, {
          city: rulingLocation.city,
          latitude: rulingLocation.latitude,
          longitude: rulingLocation.longitude,
          timezoneOffset: rulingLocation.timezoneOffset,
          date: localNow.date,
          time: localNow.time,
        });
        setCurrentRulingPlanets(response.data.data.rulingPlanets);
      } catch (error: any) {
        setCurrentRulingError(error?.response?.data?.message || 'Unable to load ruling planets');
      }
    };

    loadCurrentRulingPlanets();
  }, [rulingLocation]);
  useEffect(() => {
    if (!isPremiumUser || !chartData || kpGuidance || kpGuidanceLoading || kpGuidanceError) return;

    const loadGuidance = async () => {
      try {
        setKpGuidanceLoading(true);
        setKpGuidanceError(null);
        const token = authState.token || localStorage.getItem('nadiAstroToken') || '';
        const chartId = chartData?._id || chartData?.id;
        const response = await axios.post(
          `${API_BASE_URL}/charts/kp-guidance`,
          chartId && chartId !== 'preview' ? { chartId, language } : { chartData, language },
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        setKpGuidance(response.data.guidance);
      } catch (error: any) {
        setKpGuidanceError(error?.response?.data?.message || error?.response?.data?.error || error?.message || 'KP guidance unavailable.');
      } finally {
        setKpGuidanceLoading(false);
      }
    };

    loadGuidance();
  }, [authState.token, chartData, isPremiumUser, kpGuidance, kpGuidanceError, kpGuidanceLoading, language]);

  useEffect(() => {
    setKpGuidance(null);
    setKpGuidanceError(null);
  }, [language]);
  useEffect(() => {
    if (!isPremiumUser || !chartData || kpMuhurat || kpMuhuratLoading || kpMuhuratError) return;

    const loadMuhurat = async () => {
      try {
        setKpMuhuratLoading(true);
        setKpMuhuratError(null);
        const token = authState.token || localStorage.getItem('nadiAstroToken') || '';
        const chartId = chartData?._id || chartData?.id;
        const response = await axios.post(
          `${API_BASE_URL}/charts/kp-muhurat`,
          chartId && chartId !== 'preview' ? { chartId, language } : { chartData, language },
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        setKpMuhurat(response.data.muhurat);
      } catch (error: any) {
        setKpMuhuratError(error?.response?.data?.message || error?.response?.data?.error || error?.message || 'KP time-window factors unavailable.');
      } finally {
        setKpMuhuratLoading(false);
      }
    };

    loadMuhurat();
  }, [authState.token, chartData, isPremiumUser, kpMuhurat, kpMuhuratError, kpMuhuratLoading, language]);

  useEffect(() => {
    setKpMuhurat(null);
    setKpMuhuratError(null);
  }, [language]);
  const getActualHouseForDisplay = useCallback((displayHouse: number) => ((rotationHouse + displayHouse - 2) % 12) + 1, [rotationHouse]);
  const toRotatedHouse = useCallback((house: number) => ((house - rotationHouse + 12) % 12) + 1, [rotationHouse]);
  const rotateHouseList = useCallback(
    (houses: number[]) => houses.map((house) => toRotatedHouse(Number(house))).filter((house) => house >= 1 && house <= 12),
    [toRotatedHouse]
  );
  const signText = (sign: any) => {
    if (!sign) return '-';
    const signNumber = sign?.number || sign?.signNumber;
    if (signNumber) {
      return t(`astrology.signs.${signNumber}`, { defaultValue: sign?.name || String(signNumber) });
    }
    return sign?.name || '-';
  };
  const aspectText = (aspect: string) => t(`kp.aspectNames.${aspect}`, { defaultValue: aspect || '-' });
  const hitText = (hit: string) => t(`kp.hitNames.${hit}`, { defaultValue: hit || '-' });

  const planetRows = useMemo<PlanetRow[]>(() => {
    const planets = chartData?.planets || {};
    const kpEntries: any[] = Object.values(kpSignificatorMap);

    const orderedPlanetKeys = Object.keys(planets).sort((a, b) => {
      const ai = PLANET_ORDER.indexOf(normalizePlanet(a));
      const bi = PLANET_ORDER.indexOf(normalizePlanet(b));
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });

    return orderedPlanetKeys.map((planetKey) => {
      const planet = planets[planetKey] || {};
      const canonical = normalizePlanet(planet?.name || planetKey);
      const runtimeFineLords = typeof planet?.longitude === 'number' ? getKPSubAndSubSubLord(planet.longitude) : null;

      const entry =
        kpSignificatorMap?.[canonical] ||
        kpEntries.find((e: any) => normalizePlanet(e?.planet) === canonical) ||
        {};

      const level1 = asNumberArray(entry?.level1?.length ? entry.level1 : entry?.levels?.starLord?.significators);
      const level2 = asNumberArray(entry?.level2?.length ? entry.level2 : entry?.levels?.planet?.significators);
      const level3 = asNumberArray(entry?.level3?.length ? entry.level3 : entry?.levels?.starLordOwnership?.significators);
      const level4 = asNumberArray(entry?.level4?.length ? entry.level4 : entry?.levels?.planetOwnership?.significators);

      const all = asNumberArray(entry?.allSignificators?.length ? entry.allSignificators : uniqueNumbers([...level1, ...level2, ...level3, ...level4]));

      return {
        planet: planet?.name || canonical,
        starLord: entry?.starLord || planet?.nakshatra?.lord || '-',
        subLord: entry?.subLord || planet?.subLord || runtimeFineLords?.subLord || '-',
        subSubLord: entry?.subSubLord || entry?.levels?.subSubLord?.name || runtimeFineLords?.subSubLord || '-',
        all,
        level1,
        level2,
        level3,
        level4,
      };
    });
  }, [chartData, kpSignificatorMap]);

  const houseRows = useMemo(() => {
    const backendHouseMap = kpData?.houseSignificators || {};
    const hasBackendHouseMap = Object.keys(backendHouseMap).length > 0;

    if (hasBackendHouseMap) {
      return Array.from({ length: 12 }, (_, i) => {
        const house = i + 1;
        const actualHouse = getActualHouseForDisplay(house);
        return {
          houseNumber: house,
          actualHouse,
          links: backendHouseMap?.[actualHouse] || backendHouseMap?.[String(actualHouse)] || [],
        };
      });
    }

    const derived: Record<number, Array<{ planet: string; level: number }>> = {};
    for (let h = 1; h <= 12; h++) derived[h] = [];

    planetRows.forEach((row) => {
      row.all.forEach((house, idx) => {
        if (!derived[house]) derived[house] = [];
        derived[house].push({ planet: row.planet, level: idx + 1 });
      });
    });

    return Array.from({ length: 12 }, (_, i) => {
      const house = i + 1;
      const actualHouse = getActualHouseForDisplay(house);
      return {
        houseNumber: house,
        actualHouse,
        links: derived[actualHouse] || [],
      };
    });
  }, [kpData, planetRows, getActualHouseForDisplay]);

  const nakshatraNadiRows = useMemo(() => {
    const planetSigs = chartData?.nadiAnalysis?.planetarySignificators || {};
    const nakshatraNadi = kpData?.nakshatraNadi || {};
    const getEntry = (source: any, key: string) => source?.[key] || Object.entries(source || {}).find(([entryKey, entry]: [string, any]) => {
      const normalizedKey = normalizePlanet(entryKey);
      const normalizedPlanet = normalizePlanet(entry?.planet);
      return normalizedKey === key || normalizedPlanet === key;
    })?.[1];
    const getPlanetLevelSignificators = (name: string | undefined | null): number[] => {
      if (!name) return [];
      const matchingEntry = Object.values(planetSigs).find((entry: any) => normalizePlanet(entry?.planet) === normalizePlanet(name)) as any;
      return matchingEntry?.significators?.planet || [];
    };
    const getPlanetCurrentHouse = (name: string | undefined | null) => {
      if (!name) return undefined;
      const entry: any = getEntry(planetSigs, normalizePlanet(name)) || getEntry(nakshatraNadi, normalizePlanet(name));
      return entry?.currentHouse ? toRotatedHouse(Number(entry.currentHouse)) : undefined;
    };

    return NADI_PLANETS_ORDER.map((key) => {
      const nadiEntry: any = getEntry(planetSigs, key) || getEntry(nakshatraNadi, key);
      if (!nadiEntry) return null;
      const planetName = nadiEntry.planet || key;
      const starLord = nadiEntry.starLord;
      const subLord = nadiEntry.subLord;
      const planetLevel = getPlanetLevelSignificators(planetName);
      const starLordLevel = getPlanetLevelSignificators(starLord);
      const subLordLevel = getPlanetLevelSignificators(subLord);
      const tenantRecord = getTenantRecord(planetTenants, planetName);
      return {
        planet: planetName,
        currentHouse: nadiEntry.currentHouse ? toRotatedHouse(Number(nadiEntry.currentHouse)) : nadiEntry.currentHouse,
        actualHouse: nadiEntry.currentHouse,
        planetSigs: rotateHouseList(planetLevel.length ? planetLevel : (nadiEntry.significators?.planet || nadiEntry.planetLevel || [])),
        starLord,
        starLordCurrentHouse: getPlanetCurrentHouse(starLord),
        starLordSigs: rotateHouseList(starLordLevel.length ? starLordLevel : (nadiEntry.significators?.starLord || nadiEntry.starLordLevel || [])),
        subLord,
        subLordCurrentHouse: getPlanetCurrentHouse(subLord),
        subLordSigs: rotateHouseList(subLordLevel.length ? subLordLevel : (nadiEntry.significators?.subLord || nadiEntry.subLordLevel || [])),
        isUntenanted: isUntenantedPlanet(tenantRecord),
      };
    }).filter(Boolean);
  }, [chartData, kpData, planetTenants, rotateHouseList, toRotatedHouse]);

  const runtimeDasha = useMemo(() => calculateRuntimeDasha(chartData), [chartData]);

  const dashaData = useMemo(() => {
    if (nadi?.currentDashaPeriods) return nadi.currentDashaPeriods;
    if (!runtimeDasha) return null;
    return {
      mahadasha: runtimeDasha.currentMahadasha,
      antardasha: runtimeDasha.currentAntardasha,
      pratyantardasha: runtimeDasha.currentPratyantardasha,
      timezoneMinutes: runtimeDasha.timezoneMinutes,
    };
  }, [nadi, runtimeDasha]);

  const mahadashas: DashaEntry[] = useMemo(() => {
    if (Array.isArray(nadi?.vimshottariDashaTimeline) && nadi.vimshottariDashaTimeline.length) {
      return nadi.vimshottariDashaTimeline;
    }
    return runtimeDasha?.mahadashas || [];
  }, [nadi, runtimeDasha]);

  const getDashaEntryKey = (entry: DashaEntry | null | undefined) => {
    if (!entry?.lord) return 'none';
    const start = entry.start ? new Date(entry.start).toISOString() : 'no-start';
    return `${entry.lord}-${start}`;
  };

  const selectedMahadasha = useMemo(() => {
    if (!mahadashas.length) return null;
    const fromSelection = mahadashas.find((entry: DashaEntry) => getDashaEntryKey(entry) === selectedMahadashaKey);
    return fromSelection || dashaData?.mahadasha || mahadashas[0];
  }, [mahadashas, selectedMahadashaKey, dashaData]);

  const selectedAntardasha = useMemo(() => {
    if (!selectedMahadasha) return null;
    const antars = selectedMahadasha.antardashas || [];
    const fromSelection = antars.find((entry: DashaEntry) => getDashaEntryKey(entry) === selectedAntardashaKey);
    return fromSelection || dashaData?.antardasha || antars[0] || null;
  }, [selectedMahadasha, selectedAntardashaKey, dashaData]);

  const selectedPratyantardasha = useMemo(() => {
    if (!selectedAntardasha) return null;
    const pratys = selectedAntardasha.pratyantardashas || [];
    const fromSelection = pratys.find((entry: DashaEntry) => getDashaEntryKey(entry) === selectedPratyantardashaKey);
    return fromSelection || dashaData?.pratyantardasha || pratys[0] || null;
  }, [selectedAntardasha, selectedPratyantardashaKey, dashaData]);

  const derivedAspectPlanetRows = useMemo(() => {
    const planets = Object.values(chartData?.planets || {});
    const rows: any[] = [];
    for (let i = 0; i < planets.length; i++) {
      for (let j = i + 1; j < planets.length; j++) {
        const p1: any = planets[i];
        const p2: any = planets[j];
        if (typeof p1?.longitude !== 'number' || typeof p2?.longitude !== 'number') continue;
        const found = getAspect(p1.longitude, p2.longitude);
        if (!found) continue;
        rows.push({
          planet1: p1.name,
          planet2: p2.name,
          ...found,
        });
      }
    }
    return rows;
  }, [chartData]);

  const derivedAspectHouseRows = useMemo(() => {
    const cusps = chartData?.houses?.cusps || {};
    const rows: any[] = [];
    for (let h1 = 1; h1 <= 12; h1++) {
      for (let h2 = h1 + 1; h2 <= 12; h2++) {
        const c1 = cusps?.[h1];
        const c2 = cusps?.[h2];
        const lon1 = c1?.longitude;
        const lon2 = c2?.longitude;
        if (typeof lon1 !== 'number' || typeof lon2 !== 'number') continue;
        const found = getAspect(lon1, lon2);
        if (!found) continue;
        rows.push({
          house1: h1,
          house2: h2,
          ...found,
        });
      }
    }
    return rows;
  }, [chartData]);

  const aspectPlanetRows = kpData?.aspectTables?.planetary?.length ? kpData.aspectTables.planetary : derivedAspectPlanetRows;
  const aspectHouseRows = kpData?.aspectTables?.houses?.length ? kpData.aspectTables.houses : derivedAspectHouseRows;

  const hitCalculator = useMemo(() => {
    const selectedPlanet = chartData?.planets?.[selectedHitPlanet];
    if (!selectedPlanet || typeof selectedPlanet.longitude !== 'number') {
      return { selectedPlanet: null, houseRows: [], planetRows: [] };
    }

    const selectedLongitude = normalizeDeg(selectedPlanet.longitude);
    const normalizedTargetAngle = selectedHitDegree;

    const calcEntry = (label: string, longitude: number) => {
      const fullArc = normalizeDeg(longitude - selectedLongitude);
      const shortestAngle = getShortestAngle(longitude, selectedLongitude);
      const hit = detectHit(shortestAngle);
      const matchesSelected = Math.abs(shortestAngle - normalizedTargetAngle) <= 3;

      return {
        label,
        degree: formatArc(longitude),
        aspect: formatArc(fullArc),
        hitAngle: `${shortestAngle.toFixed(2)}°`,
        shortestAngle: Number(shortestAngle.toFixed(2)),
        hitName: hit.name,
        hitType: hit.type,
        matchesSelected,
      };
    };

    const houses = Array.from({ length: 12 }, (_, i) => i + 1)
      .map((house) => {
        const cusp = chartData?.houses?.cusps?.[house];
        if (typeof cusp?.longitude !== 'number') return null;
        return calcEntry(String(house), cusp.longitude);
      })
      .filter(Boolean);

    const planets = PLANET_ORDER
      .map((key) => {
        const planet = chartData?.planets?.[key];
        if (!planet || typeof planet.longitude !== 'number') return null;
        return calcEntry(planet.name || key, planet.longitude);
      })
      .filter(Boolean);

    return {
      selectedPlanet,
      houseRows: houses,
      planetRows: planets,
    };
  }, [chartData, selectedHitPlanet, selectedHitDegree]);

  const hiitEvents = useMemo(() => {
    if (kpData?.hiitEventTheory?.eventChecks && Object.keys(kpData.hiitEventTheory.eventChecks).length) {
      return kpData.hiitEventTheory.eventChecks;
    }

    const activeDashaPlanets = [
      dashaData?.mahadasha?.lord,
      dashaData?.antardasha?.lord,
      dashaData?.pratyantardasha?.lord,
    ].filter(Boolean).map((p: string) => normalizePlanet(p));

    const byPlanet: Record<string, number[]> = {};
    planetRows.forEach((row) => {
      byPlanet[normalizePlanet(row.planet)] = row.all;
    });

    const checks: any = {};
    Object.entries(HIIT_TARGETS).forEach(([eventName, houses]) => {
      const triggered = activeDashaPlanets.some((planet) => {
        const sigs = byPlanet[planet] || [];
        return sigs.some((house) => houses.includes(house));
      });
      checks[eventName] = { houses, dashaPlanets: activeDashaPlanets, triggered };
    });

    return checks;
  }, [kpData, dashaData, planetRows]);

  const partOfFortune = kpData?.partOfFortune;

  const rashiRows = useMemo(() => {
    const ascCusp = kpData?.kpCusps?.['1'] || kpData?.kpCusps?.[1];
    const rows: Array<{ label: string; rl: string; nl: string; sl: string; ssl: string }> = [];

    if (chartData?.houses?.ascendant) {
      const ascRuntimeFineLords = typeof chartData.houses.ascendant.longitude === 'number'
        ? getKPSubAndSubSubLord(chartData.houses.ascendant.longitude)
        : null;
      rows.push({
        label: t('astrology.ascendant'),
        rl: planetShort(ascCusp?.cuspLord || chartData.houses.ascendant.sign?.ruler),
        nl: planetShort(ascCusp?.starLord || chartData.houses.ascendant.nakshatra?.lord),
        sl: planetShort(ascCusp?.subLord || chartData.houses.ascendant.subLord),
        ssl: planetShort(ascCusp?.subSubLord || ascRuntimeFineLords?.subSubLord || '-'),
      });
    }

    PLANET_ORDER.forEach((key) => {
      const p = chartData?.planets?.[key];
      if (!p) return;
      const row = planetRows.find((pr) => normalizePlanet(pr.planet) === normalizePlanet(p.name || key));
      rows.push({
        label: p.name || key,
        rl: planetShort(p?.sign?.ruler),
        nl: planetShort(row?.starLord || p?.nakshatra?.lord),
        sl: planetShort(row?.subLord || p?.subLord),
        ssl: planetShort(row?.subSubLord || '-'),
      });
    });

    return rows;
  }, [chartData, kpData, planetRows, planetShort, t]);

  const cuspRows = useMemo(() => {
    const kpCusps = kpData?.kpCusps || {};
    const houses = chartData?.houses?.cusps || {};

    return Array.from({ length: 12 }, (_, i) => {
      const houseNum = i + 1;
      const actualHouse = getActualHouseForDisplay(houseNum);
      const houseData = houses[actualHouse] || houses[String(actualHouse)] || {};
      const kpCuspData = kpCusps[actualHouse] || kpCusps[String(actualHouse)] || {};

      return {
        house: houseNum,
        actualHouse,
        cuspLord: planetShort(kpCuspData?.cuspLord || houseData?.sign?.ruler || '-'),
        starLord: planetShort(kpCuspData?.starLord || '-'),
        subLord: planetShort(kpCuspData?.subLord || '-'),
        subSubLord: planetShort(
          kpCuspData?.subSubLord ||
          (typeof houseData?.longitude === 'number' ? getKPSubAndSubSubLord(houseData.longitude).subSubLord : '-')
        ),
      };
    });
  }, [chartData, kpData, planetShort, getActualHouseForDisplay]);

  const rotatedPlanetRows = useMemo(() => (
    planetRows.map((row) => ({
      ...row,
      all: rotateHouseList(row.all),
      level1: rotateHouseList(row.level1),
      level2: rotateHouseList(row.level2),
      level3: rotateHouseList(row.level3),
      level4: rotateHouseList(row.level4),
    }))
  ), [planetRows, rotateHouseList]);

  const formatGuidanceDate = (value: any) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? String(value)
      : formatDisplayDate(date);
  };

  const formatGuidanceWindow = (period: any) => {
    const years = period?.startYear && period?.endYear
      ? `${period.startYear}-${period.endYear}`
      : `${formatGuidanceDate(period?.start)} - ${formatGuidanceDate(period?.end)}`;
    const ages = period?.startAge != null && period?.endAge != null
      ? ` (age ${period.startAge}-${period.endAge})`
      : '';
    return `${years}${ages}`;
  };

  const renderKPGuidance = () => {
    if (!isPremiumUser) {
      return renderPremiumLock('Unlock KP Significator Notes', 'Rule-based KP house and planet notes are available after upgrade.');
    }

    if (kpGuidanceLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (kpGuidanceError) {
      return <Alert severity="warning">{kpGuidanceError}</Alert>;
    }

    if (!kpGuidance) {
      return <Alert severity="info">KP house and planet notes are not available for this chart yet.</Alert>;
    }

    const choices = kpGuidance.choices;
    const guidanceUi = kpGuidance.ui || {};
    const renderChoiceCard = (choice: any) => {
      if (!choice) return null;
      return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.75 }}>
            {localGuidanceText(choice.title, language)}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
            {localGuidanceText(choice.result, language)}
          </Typography>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {(choice.suitedFor || []).map((item: any) => (
              <Chip key={item.value} size="small" variant="outlined" label={localGuidanceText(item.label, language)} />
            ))}
          </Stack>
        </Paper>
      );
    };

    return (
      <Stack spacing={2}>
        <Alert severity="info">{localGuidanceText(kpGuidance.disclaimer, language)}</Alert>
        {choices && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {localGuidanceText(choices.title, language)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {localGuidanceText(choices.notes?.[0], language)}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>{renderChoiceCard(choices.careerChoices)}</Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {localGuidanceText(choices.workMode?.title, language)}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.dark', mb: 1 }}>
                    {localGuidanceText(choices.workMode?.label, language)}
                  </Typography>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    <Chip size="small" color="primary" label={`${localGuidanceText(guidanceUi.job || 'Job', language)} ${choices.workMode?.jobScore ?? 0}/100`} />
                    <Chip size="small" color="warning" label={`${localGuidanceText(guidanceUi.business || 'Business', language)} ${choices.workMode?.businessScore ?? 0}/100`} />
                  </Stack>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{localGuidanceText(guidanceUi.job || 'Job', language)}</Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                    {(choices.workMode?.jobRoleSuggestions || []).map((item: any, index: number) => (
                      <Chip key={`job-${index}`} size="small" variant="outlined" label={localGuidanceText(item, language)} />
                    ))}
                  </Stack>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{localGuidanceText(guidanceUi.business || 'Business', language)}</Typography>
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                    {(choices.workMode?.businessSuggestions || []).map((item: any, index: number) => (
                      <Chip key={`business-${index}`} size="small" variant="outlined" label={localGuidanceText(item, language)} />
                    ))}
                  </Stack>
                </Paper>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>{renderChoiceCard(choices.educationChoices)}</Grid>
            </Grid>
          </Paper>
        )}
        {(kpGuidance.events || []).map((event: any) => (
          <Paper key={event.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{localGuidanceText(event.label, language)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {localGuidanceText(guidanceUi.formula || 'Formula', language)}: {(event.formulaHouses || []).join('-')} • {localGuidanceText(event.guidance, language)}
                  </Typography>
                </Box>
              </Stack>
              <Typography variant="body2">{localGuidanceText(event.summary, language)}</Typography>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>{localGuidanceText(guidanceUi.topSignificators || 'Top Significators', language)}</Typography>
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {(event.topSignificators || []).map((item: any) => (
                    <Chip
                      key={`${event.key}-${item.planet}`}
                      label={`${tp(item.planet)} ${item.score}/100: ${(item.allHits || []).join(', ') || '-'}`}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.75 }}>{localGuidanceText(guidanceUi.timingWindows || 'DBA Factor Windows', language)}</Typography>
                {(event.timingWindows || []).length ? (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>{localGuidanceText(guidanceUi.period || 'Period', language)}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>DBA</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{localGuidanceText(guidanceUi.score || 'Score', language)}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{localGuidanceText(guidanceUi.activatedHouses || 'Activated Houses', language)}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {event.timingWindows.map((window: any, index: number) => (
                          <TableRow key={`${event.key}-window-${index}`}>
                            <TableCell>{formatGuidanceWindow(window.period)}</TableCell>
                            <TableCell>{[window.period?.maha, window.period?.bhukti, window.period?.antara].filter(Boolean).map(tp).join(' / ')}</TableCell>
                            <TableCell>{window.score}/100 ({window.strength})</TableCell>
                            <TableCell>{(window.activatedHouses || []).join(', ') || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">{localGuidanceText(guidanceUi.noTimingWindow || 'No strong timing window found in the available dasha range.', language)}</Typography>
                )}
              </Box>
            </Stack>
          </Paper>
        ))}
        <Typography variant="caption" color="text.secondary">{(kpGuidance.ruleTrace || []).map((item: any) => localGuidanceText(item, language)).join(' ')}</Typography>
      </Stack>
    );
  };

  const formatSlotDateTime = (value: any) => {
    if (!value) return '-';
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? String(value)
      : formatDisplayDateTime(date, { timeZone: 'UTC' });
  };

  const renderKPMuhurat = () => {
    if (!isPremiumUser) {
      return renderPremiumLock('Unlock KP Time-Window Factors', 'KP time-window house and DBA factors are available after upgrade.');
    }

    if (kpMuhuratLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 260 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (kpMuhuratError) {
      return <Alert severity="warning">{kpMuhuratError}</Alert>;
    }

    if (!kpMuhurat) {
      return <Alert severity="info">KP time-window factors are not available for this chart yet.</Alert>;
    }

    return (
      <Stack spacing={2}>
        <Alert severity="info">{localGuidanceText(kpMuhurat.disclaimer, language)}</Alert>
        {(kpMuhurat.events || []).map((event: any) => (
          <Paper key={event.key} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>{localGuidanceText(event.label, language)}</Typography>
                <Typography variant="body2" color="text.secondary">{localGuidanceText(event.summary, language)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {localGuidanceText({ en: 'Factors for this chart', hi: 'इस chart के factors' }, language)} • {t('kp.fruitful.housesLabel')}: {(event.positiveHouses || []).join('-')} • {t('kp.karaka')}: {(event.karakas || []).map(tp).join(', ')}
                </Typography>
              </Box>
              {(event.slots || []).length ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>{localGuidanceText({ en: 'Time', hi: 'समय' }, language)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>DBA</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{localGuidanceText({ en: 'Score', hi: 'स्कोर' }, language)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('kp.fruitful.whyResult')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {event.slots.map((slot: any, index: number) => (
                        <TableRow key={`${event.key}-muhurat-${index}`}>
                          <TableCell>{formatSlotDateTime(slot.start)} - {formatSlotDateTime(slot.end)}</TableCell>
                          <TableCell>{[slot.period?.maha, slot.period?.bhukti, slot.period?.antara].filter(Boolean).map(tp).join(' / ') || '-'}</TableCell>
                          <TableCell>{slot.score}/100 ({slot.grade})</TableCell>
                          <TableCell>
                            {(slot.explanation || []).slice(0, 2).map((line: string, lineIndex: number) => (
                              <Typography key={lineIndex} variant="body2" color="text.secondary">{line}</Typography>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">{localGuidanceText({ en: 'No strong personalized KP muhurat slot found in the available dasha range.', hi: 'Available dasha range में strong personalized KP muhurat slot नहीं मिला।' }, language)}</Typography>
              )}
            </Stack>
          </Paper>
        ))}
        <Typography variant="caption" color="text.secondary">{(kpMuhurat.ruleTrace || []).map((item: any) => localGuidanceText(item, language)).join(' ')}</Typography>
      </Stack>
    );
  };

  if (!chartData || !chartData.planets || !chartData.houses) {
    return (
      <Box sx={{ textAlign: 'center', p: 4 }}>
        <Typography color="text.secondary">{t('kp.noChartData')}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1d5f94 0%, #144b75 100%)',
          color: 'white',
          p: 2,
          borderRadius: 2,
          mb: 2,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Typography variant="h5" sx={{ fontWeight: 600 }}>{t('kp.krishnamurtiPaddhati')}</Typography>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.82)', mb: 0.5 }}>
              Ruling Planets · {rulingLocation.city}
            </Typography>
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {currentRulingPlanets ? rulingPlanetItems(currentRulingPlanets).map((item) => (
                <Chip
                  key={item.label}
                  size="small"
                  label={`${item.label}: ${planetShort(item.value || '-')}`}
                  title={item.title}
                  sx={{ bgcolor: 'rgba(255,255,255,0.16)', color: 'white', border: '1px solid rgba(255,255,255,0.25)' }}
                />
              )) : (
                <Typography variant="caption" sx={{ color: currentRulingError ? '#ffd6d6' : 'rgba(255,255,255,0.86)' }}>
                  {currentRulingError || 'Loading ruling planets...'}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Paper sx={{ mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ p: 1.5 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, value) => setTabIndex(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1, minWidth: 0 }}
          >
            {TAB_KEYS.map((key) => (
              <Tab key={key} value={TAB_VALUE_BY_KEY[key]} label={key === 'kp.kpChart' ? t('astrology.kpAnalysis') : key === 'kp.dashaAnalysis' ? dashaAnalysisLabel(i18n.resolvedLanguage || i18n.language) : key === 'kp.eventAnalysis' ? translateNadiEventText(i18n.resolvedLanguage || i18n.language, 'Event Analysis') : t(key, { defaultValue: TAB_LABEL_DEFAULTS[key] || key })} />
            ))}
            <Tab value={14} label={`${isPremiumUser ? '' : '🔒 '}${t('kp.nadiTransitScanner', { defaultValue: 'NADI TRANSIT SCANNER' })}`} />
          </Tabs>
          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>{t('astrology.rotateFromHouse')}</InputLabel>
            <Select
              value={rotationHouse}
              label={t('astrology.rotateFromHouse')}
              onChange={(event) => setRotationHouse(Number(event.target.value))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((house) => (
                <MenuItem key={house} value={house}>{t('astrology.house', { number: house })}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {tabIndex === 0 && (
        <Grid container spacing={2}>
          

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.d1RashiChart')}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                <FormControlLabel
                  control={<Switch checked={showTransitOverlay} onChange={(event) => setShowTransitOverlay(event.target.checked)} />}
                  label={t('astrology.showCurrentTransitOverlay')}
                  sx={{ m: 0 }}
                />
                {transitLoading ? <Typography variant="caption" color="primary">{t('astrology.loadingCurrentTransit')}</Typography> : null}
                {transitError ? <Typography variant="caption" color="error">{transitError}</Typography> : null}
              </Box>
              <NorthIndianChart
                chartData={chartData}
                transitChartData={transitChart}
                showTransitOverlay={showTransitOverlay}
                size={chartDisplaySize}
                rotationHouse={rotationHouse}
                showHouseLabels={false}
                showFortune
              />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.kpBhavaChalitChart')}</Typography>
              <BhavaChalitChart chartData={chartData} size={chartDisplaySize} rotationHouse={rotationHouse} showHouseLabels={false} />
            </Paper>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2, border: '2px solid #81c784', bgcolor: '#f6fff7' }}>
              <Typography variant="h6" sx={{ mb: 1.25, color: '#2e7d32', fontWeight: 600 }}>
                {t('astrology.planetPositions')}
              </Typography>
              <TableContainer sx={{ maxHeight: 520, border: '1px solid #c8e6c9', borderRadius: 2, bgcolor: 'white' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>{t('astrology.planet')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>RL</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>NL</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>SL</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>SSL</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rashiRows.map((row) => (
                      <TableRow key={`rashi-top-${row.label}`}>
                        <TableCell sx={{ fontWeight: row.label === t('astrology.ascendant') ? 700 : 500 }}>
                          {row.label === t('astrology.ascendant') ? row.label : tp(row.label)}
                        </TableCell>
                        <TableCell>{row.rl}</TableCell>
                        <TableCell>{row.nl}</TableCell>
                        <TableCell>{row.sl}</TableCell>
                        <TableCell>{row.ssl}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2, border: '2px solid #4caf50', bgcolor: '#f1f8f4' }}>
              <Typography variant="h6" sx={{ mb: 1.25, color: '#2e7d32', fontWeight: 600 }}>
                {t('astrology.kpCuspsAllHouses')}
              </Typography>
              <TableContainer sx={{ border: '1px solid #81c784', borderRadius: 2, bgcolor: 'white' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>{t('astrology.house', { number: '' }).trim()}</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>{t('astrology.cuspLord')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>{t('astrology.starLord')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>{t('astrology.subLord')}</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'white' }}>{t('kp.subSubLord')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cuspRows.map((row) => (
                      <TableRow key={`cusp-${row.house}`}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {row.actualHouse !== row.house ? `${row.house} (${row.actualHouse})` : row.house}
                        </TableCell>
                        <TableCell>{row.cuspLord}</TableCell>
                        <TableCell>{row.starLord}</TableCell>
                        <TableCell>{row.subLord}</TableCell>
                        <TableCell>{row.subSubLord}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2, border: '2px solid #ff9800', bgcolor: '#fff8ef' }}>
              <Typography variant="h6" sx={{ mb: 1.2, color: '#e65100', fontWeight: 600 }}>{t('kp.kpSignificators')}</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('astrology.starLord')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('astrology.subLord')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.subSubLord')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.priorityHouseSignificators')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rotatedPlanetRows.map((row) => (
                      <TableRow key={`classic-sig-${row.planet}`}>
                        <TableCell sx={{ fontWeight: 600 }}>{tp(row.planet)}</TableCell>
                        <TableCell>{lordText(row.starLord)}</TableCell>
                        <TableCell>{lordText(row.subLord)}</TableCell>
                        <TableCell>{lordText(row.subSubLord)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {row.all.length ? row.all.map((house, idx) => (
                              <Chip
                                key={`${row.planet}-${house}-${idx}`}
                                size="small"
                                label={`${idx + 1}:${house}`}
                                color={idx < 2 ? 'success' : 'default'}
                                variant={idx < 2 ? 'filled' : 'outlined'}
                              />
                            )) : <Typography variant="body2" color="text.secondary">-</Typography>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2, border: '2px solid', borderColor: 'primary.light', bgcolor: 'rgba(46,125,82,0.06)' }}>
              <Typography variant="h6" sx={{ mb: 1.5, color: 'primary.dark', fontWeight: 600 }}>
                {t('kp.class31Title')}
              </Typography>
              <Grid container spacing={1.5}>
                {rotatedPlanetRows.map((row) => (
                  <Grid key={`classic-level-${row.planet}`} size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 1.5, border: '1px solid', borderColor: 'primary.light' }}>
                      <Typography sx={{ fontWeight: 600, color: 'primary.dark', mb: 1 }}>{tp(row.planet)}</Typography>
                      <Typography variant="body2">{t('kp.level', { number: 1 })}: {row.level1.join(', ') || '-'}</Typography>
                      <Typography variant="body2">{t('kp.level', { number: 2 })}: {row.level2.join(', ') || '-'}</Typography>
                      <Typography variant="body2">{t('kp.level', { number: 3 })}: {row.level3.join(', ') || '-'}</Typography>
                      <Typography variant="body2">{t('kp.level', { number: 4 })}: {row.level4.join(', ') || '-'}</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600 }}>{t('kp.all')}: {row.all.join(', ') || '-'}</Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.25 }}>{t('kp.houseCuspsPlacidus')}</Typography>
              <Grid container spacing={1}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((house) => {
                  const cusp = chartData?.houses?.cusps?.[house];
                  const deg = cusp?.degree?.formatted || (typeof cusp?.longitude === 'number' ? `${cusp.longitude.toFixed(2)}°` : '-');
                  return (
                    <Grid key={`cusp-${house}`} size={{ xs: 6 }}>
                      <Paper sx={{ p: 1, textAlign: 'center', bgcolor: house === 1 ? 'rgba(46,125,82,0.12)' : '#f7f7f7' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{t('astrology.house', { number: house })}</Typography>
                        <Typography variant="body2">{deg}</Typography>
                        <Typography variant="caption" color="text.secondary">{signText(cusp?.sign)}</Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1.25 }}>{t('kp.bhavaChalitEqualHouseSystem')}</Typography>
              <Grid container spacing={1}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((house) => {
                  const cusp = chartData?.bhavaChalit?.cusps?.[house];
                  const deg = cusp?.degree?.formatted || (typeof cusp?.longitude === 'number' ? `${cusp.longitude.toFixed(2)}°` : '-');
                  return (
                    <Grid key={`bc-cusp-${house}`} size={{ xs: 6 }}>
                      <Paper sx={{ p: 1, textAlign: 'center', bgcolor: '#f6faf7' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{t('astrology.bhava')} {house}</Typography>
                        <Typography variant="body2">{deg}</Typography>
                        <Typography variant="caption" color="text.secondary">{signText(cusp?.sign)}</Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t('kp.navtaraSource')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('kp.navtaraDescription')}
              </Typography>
              {partOfFortune ? (
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`${t('astrology.partOfFortune')}: ${partOfFortune.longitude}°`} color="primary" />
                  <Chip label={`${t('astrology.sign')}: ${partOfFortune.signNumber || '-'}`} variant="outlined" />
                  <Chip label={partOfFortune.isDayChart ? t('kp.dayChartFormula') : t('kp.nightChartFormula')} variant="outlined" />
                </Box>
              ) : null}
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabIndex === 2 && (
        <Box>
          {renderKPGuidance()}
        </Box>
      )}

      {tabIndex === 5 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.houseSignificator')}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('astrology.house', { number: '' }).trim()}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('kp.linkedPlanets')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {houseRows.map((row) => (
                  <TableRow key={`house-${row.houseNumber}`}>
                    <TableCell>
                      {row.actualHouse && row.actualHouse !== row.houseNumber ? `${row.houseNumber} (${row.actualHouse})` : row.houseNumber}
                    </TableCell>
                    <TableCell>
                      {row.links.length ? (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                          {row.links.map((item: any, idx: number) => (
                            <Chip
                              key={`${row.houseNumber}-${item.planet}-${idx}`}
                              size="small"
                              label={`${tp(item.planet)} (${t('kp.levelAbbr', { number: item.level || idx + 1 })})`}
                              color={idx < 2 ? 'primary' : 'default'}
                              variant={idx < 2 ? 'filled' : 'outlined'}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabIndex === 6 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.planetSignificator')}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('astrology.starLord')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('astrology.subLord')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('kp.allSignificators')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rotatedPlanetRows.map((row) => (
                  <TableRow key={`planet-${row.planet}`}>
                    <TableCell sx={{ fontWeight: 600 }}>{tp(row.planet)}</TableCell>
                    <TableCell>{lordText(row.starLord)}</TableCell>
                    <TableCell>{lordText(row.subLord)}</TableCell>
                    <TableCell>{row.all.join(', ') || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5, mt: 2 }}>
            {rotatedPlanetRows
              .filter((row) => ['RAHU', 'KETU'].includes(normalizePlanet(row.planet)))
              .map((row) => (
                <Paper
                  key={`node-abcd-${row.planet}`}
                  sx={{ p: 1.5, border: '1px solid #ffcc80', bgcolor: '#fff8e1' }}
                >
                  <Typography sx={{ fontWeight: 600, color: '#e65100', mb: 0.75 }}>
                    {tp(row.planet)} {t('kp.abcdSignification', { defaultValue: 'A/B/C/D Signification' })}
                  </Typography>
                  <Typography variant="body2">A: {row.level1.join(', ') || '-'}</Typography>
                  <Typography variant="body2">B: {row.level2.join(', ') || '-'}</Typography>
                  <Typography variant="body2">C: {row.level3.join(', ') || '-'}</Typography>
                  <Typography variant="body2">D: {row.level4.join(', ') || '-'}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    {t('kp.rahuKetuAgencyNote', { defaultValue: 'Rahu/Ketu are also read through node agency including sign lord, star lord, sub lord and available conjunction/aspect links.' })}
                  </Typography>
                </Paper>
              ))}
          </Box>
        </Paper>
      )}


      {tabIndex === 1 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 0.5 }}>{t('astrology.nakshatraNadi')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('astrology.planetStarSub')}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="kp-life-event-label">{t('astrology.lifeEvent')}</InputLabel>
            <Select
              labelId="kp-life-event-label"
              value={effectiveLifeEventKey}
              label={t('astrology.lifeEvent')}
              onChange={(event) => {
                const nextEvent = event.target.value;
                if (isPremiumUser || FREE_LIFE_EVENT_KEYS.has(nextEvent)) {
                  setSelectedLifeEvent(nextEvent);
                }
              }}
              MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
            >
              {Object.entries(lifeEvents).map(([key, event]) => (
                <MenuItem key={key} value={key} disabled={!isPremiumUser && !FREE_LIFE_EVENT_KEYS.has(key)}>
                  {event.label}
                  {!isPremiumUser && !FREE_LIFE_EVENT_KEYS.has(key) ? ' (Locked)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!isPremiumUser && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 1 }}>
                Upgrade to unlock more life events
              </Alert>
              <Button size="small" variant="contained" color="warning" onClick={() => navigate('/upgrade')}>
                Upgrade
              </Button>
            </Box>
          )}
          {effectiveLifeEventKey !== 'none' && (
            <Box sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'primary.main', borderRadius: 1, bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
                {t('astrology.houseSignificanceFor', { event: selectedLifeEventDefinition.label })}
              </Typography>
              <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
                {[
                  { label: t('astrology.good'), houses: selectedLifeEventDefinition.goodHouses, color: '#4caf50' },
                  { label: t('astrology.goodSupportive'), houses: selectedLifeEventDefinition.goodSupportiveHouses, color: '#2196f3' },
                  { label: t('astrology.bad'), houses: selectedLifeEventDefinition.badHouses, color: '#f44336' },
                  { label: t('astrology.badSupportive'), houses: selectedLifeEventDefinition.badSupportiveHouses, color: '#8d6e63' },
                ].filter((item) => item.houses.length).map((item) => (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: item.color }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {item.label}: {item.houses.join(', ')}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
          <Grid container spacing={1.5}>
            {nakshatraNadiRows.map((row: any) => (
              <Grid key={`kp-nadi-${row.planet}`} size={{ xs: 12, sm: 6, md: 4 }}>
                <Paper
                  sx={{
                    p: 1.5,
                    border: '2px solid',
                    borderColor: row.isUntenanted ? 'warning.main' : 'divider',
                    bgcolor: row.isUntenanted ? 'rgba(245, 158, 11, 0.10)' : 'background.paper',
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: row.isUntenanted ? 'warning.dark' : 'primary.main', textDecoration: 'underline', textAlign: 'center', mb: 0.75 }}>
                    {planetShort(row.planet)}{row.isUntenanted ? '*' : ''}{row.currentHouse ? ` (H${row.currentHouse}${row.actualHouse && row.actualHouse !== row.currentHouse ? `/H${row.actualHouse}` : ''})` : ''}
                  </Typography>
                  <Typography variant="body2"><strong>{planetShort(row.planet)}</strong> {renderLifeEventHouses(row.planetSigs, row.currentHouse)}</Typography>
                  <Typography variant="body2"><strong>{planetShort(row.starLord)}</strong> {renderLifeEventHouses(row.starLordSigs, row.starLordCurrentHouse)}</Typography>
                  <Typography variant="body2"><strong>{planetShort(row.subLord)}</strong> {renderLifeEventHouses(row.subLordSigs, row.subLordCurrentHouse)}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="caption" color="text.secondary">
              * Planet is not the Star Lord of any other planet.
            </Typography>
          </Grid>
        </Paper>
      )}

      {tabIndex === 16 && (
        <EventAnalysisPanel chart={chartData} isPremium={isPremiumUser} rotationHouse={rotationHouse} />
      )}

      {tabIndex === 14 && (
        <KpNadiTransitScanner
          chartData={chartData}
          isPremium={isPremiumUser}
          token={authState.token || localStorage.getItem('nadiAstroToken')}
          onUpgrade={() => navigate(authState.user ? '/upgrade' : '/login')}
        />
      )}

      {tabIndex === 13 && <DashaAnalysisPanel chart={chartData} system="KP" />}
      {tabIndex === 15 && <FaladeshSystemPanel chartId={chartData?._id || chartData?.id || chartData?.chartId} system="KP" />}

      {tabIndex === 7 && (
        isPremiumUser
          ? <KpEphemerisPanel chartData={chartData} />
          : renderPremiumLock('Unlock KP Ephemeris', 'KP Ephemeris tables are available after upgrade.')
      )}

      {tabIndex === 8 && (
        isPremiumUser
          ? <KpTransitPanel chartData={chartData} />
          : renderPremiumLock('Unlock KP Transit', 'KP Transit planets, cusps and ruling planets are available after upgrade.')
      )}

      {tabIndex === 9 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.fourLevelPlanetarySignificatorSystem')}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('kp.level', { number: 1 })}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('kp.level', { number: 2 })}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('kp.level', { number: 3 })}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('kp.level', { number: 4 })}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rotatedPlanetRows.map((row) => (
                  <TableRow key={`level-${row.planet}`}>
                    <TableCell sx={{ fontWeight: 600 }}>{tp(row.planet)}</TableCell>
                    <TableCell>{row.level1.join(', ') || '-'}</TableCell>
                    <TableCell>{row.level2.join(', ') || '-'}</TableCell>
                    <TableCell>{row.level3.join(', ') || '-'}</TableCell>
                    <TableCell>{row.level4.join(', ') || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tabIndex === 10 && (
        isPremiumUser ? (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.planetaryAspectTable')}</Typography>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>P1</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>P2</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.aspect')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Orb</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aspectPlanetRows.map((row: any, index: number) => (
                      <TableRow key={`pa-${index}`}>
                        <TableCell>{tp(row.planet1)}</TableCell>
                        <TableCell>{tp(row.planet2)}</TableCell>
                        <TableCell>{aspectText(row.aspect)} ({row.aspectDegree}°)</TableCell>
                        <TableCell>{row.orb}°</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.houseAspectTable')}</Typography>
              <TableContainer sx={{ maxHeight: 420 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>H1</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>H2</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.aspect')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Orb</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {aspectHouseRows.map((row: any, index: number) => (
                      <TableRow key={`ha-${index}`}>
                        <TableCell>{getHouseShort(t, row.house1)}</TableCell>
                        <TableCell>{getHouseShort(t, row.house2)}</TableCell>
                        <TableCell>{aspectText(row.aspect)} ({row.aspectDegree}°)</TableCell>
                        <TableCell>{row.orb}°</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
        ) : renderPremiumLock('Unlock KP Aspects', 'Planetary and house aspect tables are available after upgrade.')
      )}

      {tabIndex === 11 && (
        isPremiumUser ?
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.hitTheoryCalculator')}</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="hit-planet-label">{t('kp.selectPlanet')}</InputLabel>
                <Select
                  labelId="hit-planet-label"
                  value={selectedHitPlanet}
                  label={t('kp.selectPlanet')}
                  onChange={(event) => setSelectedHitPlanet(String(event.target.value))}
                >
                  {PLANET_ORDER.filter((key) => chartData?.planets?.[key]).map((key) => (
                    <MenuItem key={key} value={key}>{tp(chartData.planets[key]?.name || key)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600 }}>{t('kp.selectDegree')}</Typography>
              <RadioGroup
                row
                value={String(selectedHitDegree)}
                onChange={(event) => setSelectedHitDegree(Number(event.target.value))}
                sx={{ gap: 1, flexWrap: 'wrap' }}
              >
                {HIT_DEGREE_OPTIONS.map((degree) => (
                  <FormControlLabel key={degree} value={String(degree)} control={<Radio size="small" />} label={`${degree}`} />
                ))}
              </RadioGroup>
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('astrology.house', { number: '' }).trim()}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.degree')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.aspect')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.hitAngle', { defaultValue: 'Hit Angle' })}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Hit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hitCalculator.houseRows.map((row: any) => (
                      <TableRow key={`hit-house-${row.label}`} sx={row.matchesSelected ? { bgcolor: '#e8f5e9' } : undefined}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{row.degree}</TableCell>
                        <TableCell>{row.aspect}</TableCell>
                        <TableCell>{row.hitAngle}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={hitText(row.hitName)}
                            color={row.hitType === 'positive' ? 'success' : row.hitType === 'negative' ? 'warning' : 'default'}
                            variant={row.matchesSelected ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>{t('astrology.planet')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.degree')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.aspect')}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{t('kp.hitAngle', { defaultValue: 'Hit Angle' })}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Hit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hitCalculator.planetRows.map((row: any) => (
                      <TableRow key={`hit-planet-${row.label}`} sx={row.matchesSelected ? { bgcolor: '#e8f5e9' } : undefined}>
                        <TableCell>{tp(row.label)}</TableCell>
                        <TableCell>{row.degree}</TableCell>
                        <TableCell>{row.aspect}</TableCell>
                        <TableCell>{row.hitAngle}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={hitText(row.hitName)}
                            color={row.hitType === 'positive' ? 'success' : row.hitType === 'negative' ? 'warning' : 'default'}
                            variant={row.matchesSelected ? 'filled' : 'outlined'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          <Typography variant="h6" sx={{ mb: 1.5 }}>{t('kp.hitEventTriggerChecks')}</Typography>
          <Grid container spacing={1.25}>
            {Object.entries(hiitEvents).map(([eventName, data]: any) => (
              <Grid size={{ xs: 12, md: 6 }} key={eventName}>
                <Paper
                  sx={{
                    p: 1.5,
                    border: '1px solid',
                    borderColor: data?.triggered ? 'success.main' : 'warning.main',
                    bgcolor: data?.triggered ? '#e8f5e9' : '#fff8e1',
                  }}
                >
                  <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{eventName}</Typography>
                  <Typography variant="body2">{t('kp.targetHouses')}: {(data?.houses || []).join(', ')}</Typography>
                  <Typography variant="body2">{t('kp.dashaPlanets')}: {(data?.dashaPlanets || []).map((planet: string) => tp(planet)).join(', ') || '-'}</Typography>
                  <Chip
                    label={data?.triggered ? t('kp.triggered') : t('kp.notTriggered')}
                    size="small"
                    color={data?.triggered ? 'success' : 'warning'}
                    sx={{ mt: 1 }}
                  />
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper> : (
          renderPremiumLock('Unlock KP HIT Theory', 'KP HIT Theory analysis is available after upgrade.')
        )
      )}

      {tabIndex === 12 && (
        isPremiumUser ? (
          <KpFruitfulSignificatorPanel
            chartData={chartData}
            apiBaseUrl={API_BASE_URL}
            judgmentLocation={rulingLocation}
          />
        ) : renderPremiumLock(
          t('kp.fruitfulAnalyzer.premiumTitle'),
          t('kp.fruitfulAnalyzer.premiumMessage')
        )
      )}

      {tabIndex === 3 && (
        <Box>
          {renderKPMuhurat()}
        </Box>
      )}

      {tabIndex === 4 && (
        <Box sx={{ p: 2, bgcolor: '#eef8f0', border: '2px solid #2e7d52', borderRadius: 3 }}>
          <Typography variant="h6" sx={{ color: '#1b5e20', fontWeight: 'bold', mb: 0.5 }}>
            {t('kp.dashas')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {t('kp.dashaTimelineHint', { defaultValue: 'Nadi-style timeline: Mahadasha -> Antardasha -> Pratyantardasha' })}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`${t('kp.mahadasha')}: ${lordText(dashaData?.mahadasha?.lord || selectedMahadasha?.lord || '-')}`} color="success" variant="outlined" />
            <Chip label={`${t('kp.antardasha')}: ${lordText(dashaData?.antardasha?.lord || selectedAntardasha?.lord || '-')}`} color="success" variant="outlined" />
            <Chip label={`${t('kp.pratyantardasha')}: ${lordText(dashaData?.pratyantardasha?.lord || selectedPratyantardasha?.lord || '-')}`} color="success" variant="outlined" />
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: '#1b5e20', fontWeight: 600, mb: 1 }}>
                {t('kp.mahadasha')}
              </Typography>
              <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                {mahadashas.map((entry: DashaEntry) => {
                  const active = getDashaEntryKey(entry) === getDashaEntryKey(selectedMahadasha);
                  return (
                    <Box
                      key={getDashaEntryKey(entry)}
                      onClick={() => {
                        setSelectedMahadashaKey(getDashaEntryKey(entry));
                        setSelectedAntardashaKey(null);
                        setSelectedPratyantardashaKey(null);
                      }}
                      sx={{
                        p: 1,
                        mb: 0.75,
                        bgcolor: active ? '#e3f2e7' : 'white',
                        border: `1px solid ${active ? '#2e7d52' : '#b7d8c1'}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#2e7d52' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1b5e20' }}>{lordText(entry.lord || '-')}</Typography>
                        {entry.years ? <Typography variant="caption" color="text.secondary">{Number(entry.years).toFixed(2)}y</Typography> : null}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {entry.start ? formatDashaDate(new Date(entry.start), dashaData?.timezoneMinutes || 0) : '-'} - {entry.end ? formatDashaDate(new Date(entry.end), dashaData?.timezoneMinutes || 0) : '-'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 600, mb: 1 }}>
                {t('astrology.antardashaIn', { planet: lordText(selectedMahadasha?.lord || '-') })}
              </Typography>
              <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                {(selectedMahadasha?.antardashas || []).map((entry: DashaEntry) => {
                  const active = getDashaEntryKey(entry) === getDashaEntryKey(selectedAntardasha);
                  return (
                    <Box
                      key={getDashaEntryKey(entry)}
                      onClick={() => {
                        setSelectedAntardashaKey(getDashaEntryKey(entry));
                        setSelectedPratyantardashaKey(null);
                      }}
                      sx={{
                        p: 1,
                        mb: 0.75,
                        bgcolor: active ? '#edf7ee' : 'white',
                        border: `1px solid ${active ? '#388e3c' : '#c8e6c9'}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#388e3c' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>{lordText(entry.lord || '-')}</Typography>
                        {entry.years ? <Typography variant="caption" color="text.secondary">{Number(entry.years).toFixed(2)}y</Typography> : null}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {entry.start ? formatDashaDate(new Date(entry.start), dashaData?.timezoneMinutes || 0) : '-'} - {entry.end ? formatDashaDate(new Date(entry.end), dashaData?.timezoneMinutes || 0) : '-'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <Typography variant="subtitle2" sx={{ color: '#43a047', fontWeight: 600, mb: 1 }}>
                {t('astrology.pratyantardashaIn', { planet: lordText(selectedAntardasha?.lord || '-') })}
              </Typography>
              <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                {(selectedAntardasha?.pratyantardashas || []).map((entry: DashaEntry) => {
                  const active = getDashaEntryKey(entry) === getDashaEntryKey(selectedPratyantardasha);
                  return (
                    <Box
                      key={getDashaEntryKey(entry)}
                      onClick={() => setSelectedPratyantardashaKey(getDashaEntryKey(entry))}
                      sx={{
                        p: 1,
                        mb: 0.75,
                        bgcolor: active ? '#f1faf2' : 'white',
                        border: `1px solid ${active ? '#43a047' : '#dcedc8'}`,
                        borderRadius: 2,
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#43a047' },
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#43a047' }}>{lordText(entry.lord || '-')}</Typography>
                        {entry.years ? <Typography variant="caption" color="text.secondary">{Number(entry.years).toFixed(2)}y</Typography> : null}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {entry.start ? formatDashaDate(new Date(entry.start), dashaData?.timezoneMinutes || 0) : '-'} - {entry.end ? formatDashaDate(new Date(entry.end), dashaData?.timezoneMinutes || 0) : '-'}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default KPChart;
